import React, { memo, useRef, useEffect, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import nl from 'date-fns/locale/nl';
import ArrowDownIcon from '../svg/ArrowDownIcon';
import CloseIcon from '../svg/CloseIcon';
registerLocale('nl', nl);

import { today, getThisWeekend } from '../../utils/helpers';
import config from '../../Config';

const FilterbarMob = ({
	memoizedActiveFilterDate,
	memoizedActiveTimestamp,
	memoizedDateRange,
	memoizedActiveCategories,
	memoizedActiveTags,

	availableDates,
	availableCategories,
	availableTags,
	activeTab,
	availableTabs,

	setDateParam,
	setDateRangeParam,
	setCategoriesParam,
	setTagsParam,
	setActiveTags,
	resetParams,
	showModal,
	setShowModal,
	scrollToTop,
	rootElement,
	filteredElements,
}) => {
	const ref = useRef(null);

	const toggleModalWithScroll = (modalId) => {
		setShowModal((prevModal) => {
			const newModal = prevModal === modalId ? null : modalId;
			if (newModal) {
				scrollToTop(false);
			}
			return newModal;
		});
	};

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (ref.current && !ref.current.contains(event.target) && !event.target.closest('.btn') && !event.target.closest('.agenda-filter-box__close')) {
				setShowModal(null);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [ref, setShowModal]);

	useEffect(() => {
		if (showModal) {
			document.body.classList.add('open-modal');
		} else {
			document.body.classList.remove('open-modal');
		}
	}, [showModal]);

	const handlePresetDateClick = (preset) => {
		if (preset === 'today') {
			setDateParam(today);
			setDateRangeParam(null);
		} else if (preset === 'this week') {
			setDateParam(today);
			setDateRangeParam('week');
		} else if (preset === 'this month') {
			setDateParam(today);
			setDateRangeParam('month');
		} else if (preset === 'this weekend') {
			setDateParam(getThisWeekend(today));
			setDateRangeParam('weekend');
		}
	};

	const isActivePreset = (preset) => {
		if (memoizedActiveFilterDate) {
			if (memoizedActiveTimestamp === today) {
				if (memoizedDateRange) {
					if (`this ${memoizedDateRange}` === preset) return true;
				} else {
					if (preset === 'today') return true;
				}
			}
			if (memoizedActiveTimestamp === getThisWeekend(today)) {
				if (preset === 'this weekend' && memoizedDateRange === 'weekend') return true;
			}
		}
	};

	const handleDatepickerClick = (date) => {
		setDateParam(date.getTime());
		setDateRangeParam(null);
	};

	const handleParamClick = (value, group) => {
		const [formattedValue] = Object.values(value);

		if (group === 'category') {
			setCategoriesParam(formattedValue);
		}

		if (group === 'tag') {
			const activeTagsValues = memoizedActiveTags.map((tag) => Object.values(tag)[0]);
			const isActive = activeTagsValues.includes(formattedValue);

			if (isActive) {
				setActiveTags(memoizedActiveTags.filter((tag) => Object.values(tag)[0] !== formattedValue));
			} else {
				setActiveTags([...memoizedActiveTags, value]);
			}
		}
	};

	// Main function to get parameter labels based on the filter type
	// In FilterbarMob.js
	// Modify the getParamLabels function to filter tags and categories based on activeTab

	const getParamLabels = (filterType) => {
		let availableItems;
		switch (filterType) {
			case 'category':
				availableItems =
					activeTab === 'series'
						? availableCategories.filter((category) => category.source === 'bundle')
						: availableCategories.filter((category) => category.source === 'event' || !category.source);
				break;
			case 'tag':
				availableItems = activeTab === 'series' ? availableTags.filter((tag) => tag.source === 'bundle') : availableTags.filter((tag) => tag.source === 'event' || !tag.source);
				break;
			default:
				availableItems = [];
		}

		const activeItems = filterType === 'category' ? memoizedActiveCategories : memoizedActiveTags.map((tag) => Object.values(tag)[0]);

		const mappedItems = availableItems.map((item) => {
			const name = typeof item === 'string' ? item : item.name || item.label || Object.values(item)[0]; // meer expliciet

			return {
				value: item,
				name,
				active: activeItems.includes(name),
				category: item.category,
			};
		});

		return mappedItems;
	};

	const handleTagClass = (param) => {
		let className = 'tag btn';
		const isActive = memoizedActiveTags.some((tag) => Object.values(tag)[0] === param.name);

		if (isActive) {
			className += ' tag--active';
		}
		return className;
	};

	const handleCategoryClass = (category) => {
		let className = 'tag btn';
		if (category.active) {
			className += ' tag--active';
		}
		return className;
	};

	const isAllGenresActive = memoizedActiveTags.length === 0 && memoizedActiveCategories.length === 0;

	const genres = [...new Set(getParamLabels('tag').map(({ category }) => category))].map((category) => ({
		name: category,
		subgenres: getParamLabels('tag').filter((item) => item.category === category),
	}));

	return (
		<>
			<div className="agenda-filters">
				<div className="agenda-filters__container">
					{activeTab !== 'series' && (
						<div className="agenda-filter-box agenda-filter-box__datepicker">
							<div className="agenda-filter-box__tags-container">
								{config.date_filter_range &&
									config.date_presets &&
									config.date_presets.map((preset, index) => (
										<button key={index} onClick={() => handlePresetDateClick(Object.keys(preset)[0])} className={isActivePreset(Object.keys(preset)[0]) ? 'tag--active tag btn' : 'tag btn'}>
											<span className="tag__text">{Object.values(preset)[0]}</span>
										</button>
									))}
							</div>

							<button
								onClick={(event) => {
									event.preventDefault();
									toggleModalWithScroll('datePicker');
								}}
								role="button"
								className={`agenda-filters__btn btn btn--secondary ${showModal === 'datePicker' ? 'open' : ''}`}
								aria-label="Choose date"
							>
								<span>{customData.i18n.agenda_date_choose} </span>
								<span className="agenda-filters__btn-icon">
									<ArrowDownIcon />
								</span>
							</button>
							{showModal === 'datePicker' && (
								<div ref={ref} className=" agenda-filter-box__tags-modal-pick-date agenda-filters__pick-date-container">
									<div className="agenda-filter-box__tags-container--hide_mobile">
										{config.date_filter_range &&
											config.date_presets &&
											config.date_presets.map((preset, index) => (
												<button
													key={index}
													onClick={() => handlePresetDateClick(Object.keys(preset)[0])}
													className={isActivePreset(Object.keys(preset)[0]) ? 'tag--active tag agenda-filter-box__tag btn' : 'agenda-filter-box__tag tag btn'}
												>
													<span className="tag__text">{Object.values(preset)[0]}</span>
												</button>
											))}
									</div>
									<DatePicker
										selected={memoizedActiveTimestamp}
										onChange={(date) => handleDatepickerClick(date)}
										dateFormat="dd/MM/yyy"
										locale="nl"
										weekStartsOn={2}
										includeDates={availableDates.map((date, index) => {
											if (date >= today) {
												return date;
											}
										})}
										inline
									/>
								</div>
							)}
						</div>
					)}
					<div className="agenda-filter-box agenda-filter-box--genre">
						<div className="agenda-filter-box__tags-container">
							{(() => {
								const desiredOrder = ['Klassiek', 'Classic', 'Pop', 'Jazz', 'Wereld', 'Global', 'Kids'];
								return getParamLabels('category')
									.filter((category) => category.name !== 'Leeg' && category.name !== 'Empty')
									.sort((a, b) => {
										const indexA = desiredOrder.findIndex((name) => name === a.name);
										const indexB = desiredOrder.findIndex((name) => name === b.name);
										return indexA - indexB;
									})
									.map((category, categoryIndex) => {
										const isActive = handleCategoryClass(category).includes('tag--active');
										return (
											<button
												key={categoryIndex}
												onClick={() => handleParamClick(category.value, 'category')}
												className={`${handleCategoryClass(category)} tag--sub-genre`}
												aria-pressed={isActive}
												aria-label={isActive ? `Filter: ${category.name}` : undefined}
											>
												<span className="tag__text">{category.name}</span>
											</button>
										);
									});
							})()}
						</div>
						{getParamLabels('tag').length > 0 && (
							<>
								<button
									onClick={() => toggleModalWithScroll('resultsModal')}
									role="button"
									className={`agenda-filters__btn btn btn--secondary ${showModal === 'resultsModal' ? 'open' : ''}`}
									aria-label=""
								>
									<span>{customData.i18n.agenda_dropdown_more_genres}</span>
									<span className="agenda-filters__btn-icon">
										<ArrowDownIcon />
									</span>
								</button>
								{showModal === 'resultsModal' && (
									<div ref={ref} className="agenda-filter-box__tags-modal">
										{(() => {
											const desiredOrder = ['Klassiek', 'Classic', 'Pop', 'Jazz', 'Wereld', 'Global', 'Kids'];

											return genres
												.filter((category) => category.name !== 'Leeg' && category.name !== 'Empty')
												.sort((a, b) => {
													const indexA = desiredOrder.findIndex((name) => name === a.name);
													const indexB = desiredOrder.findIndex((name) => name === b.name);
													return indexA - indexB;
												})
												.map(({ name, subgenres }, genreIndex) => {
													const displayName = name === 'Leeg' ? 'Overig' : name;
													return (
														<div key={genreIndex} className="agenda-filter-box__tags-wrap">
															<h2 className="agenda-filter-box__title heading-time">{displayName}</h2>
															<div className="agenda-filter-box__tags-wrap-container">
																{subgenres.map((param, paramIndex) => {
																	const isActive = handleTagClass(param).includes('tag--active');
																	return (
																		<button
																			key={paramIndex}
																			onClick={() => handleParamClick(param.value, 'tag')}
																			className={`tag-filter ${handleTagClass(param)}`}
																			aria-pressed={isActive}
																			aria-label={isActive ? `Filter: ${param.name}` : undefined}
																		>
																			<span className="tag__text" dangerouslySetInnerHTML={{ __html: param.name }}></span>
																		</button>
																	);
																})}
															</div>
														</div>
													);
												});
										})()}
									</div>
								)}
							</>
						)}
					</div>

					{/* //only mobile */}
					<div className="agenda-filter-box agenda-filter-box--genre only-mob">
						<>
							<div className="">
								<button
									onClick={() => toggleModalWithScroll('resultsModalMobile')}
									role="button"
									className={`agenda-filters__btn btn btn--secondary ${showModal === 'resultsModal' ? 'open' : ''}`}
									aria-label=""
								>
									<span>{customData.i18n.agenda_dropdown_all_genres}</span>
									<span className="agenda-filters__btn-icon">
										<ArrowDownIcon />
									</span>
								</button>
							</div>
						</>
					</div>
					{!isAllGenresActive && (
						<button className="agenda__wis-filter" onClick={() => resetParams(true)}>
							<CloseIcon />
							<span className="agenda__wis-filter-text">{customData.i18n.agenda_empty_filter}</span>
						</button>
					)}
				</div>
			</div>
			{showModal === 'resultsModalMobile' && (
				<div ref={ref} className="agenda-filter-box__tags-modal--show-on-mobile" aria-hidden={showModal !== 'resultsModalMobile'}>
					<div className="agenda-filter-box__header">
						<div className="logo">
							<svg aria-hidden="true" fill="none" xmlns="http://www.w3.org/2000/svg" width="92" height="36">
								<path
									d="M35.79.891 33.841 3.56l.05 31.663H21.105v-.306l2.776-2.667-.05-24.219h-.307l-8.576 21.095h-.564L4.26 8.486h-.257v3.438c0 11.032 3.339 17.857 9.704 23.042v.256H2.874V6.026L0 .891V.585l11.088-.1 7.442 15.703h.257L25.058.585H35.79V.89Zm27.255 1.591c-3.325-.043-5.858-.435-8.79-1.184l-.129.264c3.632 1.312 5.116 3.936 5.116 6.732 0 6.91-8.833 8.48-9.882 8.48-2.276 0-4.374-.614-5.993-1.705-.264.306-.478.784-.478 1.576 0 1.048.392 2.93 4.637 2.93l6.472.044c4.809 0 7.085 2.489 7.085 5.598 0 4.721-5.38 10.013-12.814 10.013-4.81 0-9.797-1.441-11.81-5.07-.22-.436 2.405-2.84 5.295-5.378-2.533-.963-4.024-2.931-4.024-5.249 0-.57 2.797-2.795 5.073-4.85-1.884-1.483-3.06-3.63-3.06-6.118C39.742 1.262 49.06 0 49.495 0c1.883 0 3.803.613 6.607.613h6.956v1.883l-.014-.014ZM45.507 25.474c-1.177 0-2.276-.172-3.19-.478-.263.35-.392.699-.392 1.134 0 3.323 7.963 4.329 11.895 4.329 3.282 0 5.643-.92 5.643-2.36 0-1.27-1.398-2.276-5.815-2.404l-8.134-.221h-.007Zm6.557-16.738c-1.662-4.942-3.325-7.388-5.115-7.303-1.577.043-1.97 1.883-.307 6.775 1.662 4.764 3.367 7.26 5.115 7.26 1.577-.043 2.013-1.883.307-6.732ZM92 20.653V35.23H63.402v-.307l2.776-2.667V3.566L63.402.899V.592h28.29v14.577h-.306l-2.875-4.465C83.887 3.266 82.71 1.52 76.702 1.52h-.514v15.804h.514c2.776 0 4.773-.82 8.063-7.082h.307v15.033h-.307c-3.232-5.848-5.287-7.081-8.063-7.081h-.514V34.31h.82c6.009 0 7.186-1.748 11.81-9.186.82-1.283 1.748-2.774 2.875-4.464H92v-.007Z"
									fill="#fff"
								/>
							</svg>
						</div>

						<button aria-label="close modal" className="agenda-filter-box__close" onClick={() => setShowModal(null)}>
							Close
							<svg aria-hidden="true" width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
								<rect width="40" height="40" rx="20" fill="#09015F" />
								<path fillRule="evenodd" clipRule="evenodd" d="M25.25 25.9998L14.0002 14.75L14.7502 14L26 25.2498L25.25 25.9998Z" fill="white" stroke="white" />
								<path fillRule="evenodd" clipRule="evenodd" d="M14 25.2499L25.2498 14.0001L25.9998 14.7501L14.75 25.9999L14 25.2499Z" fill="white" stroke="white" />
							</svg>
						</button>
					</div>
					<div className="agenda-filter-box__genre-container">
						{(() => {
							const desiredOrder = ['Klassiek', 'Classic', 'Pop', 'Jazz', 'Wereld', 'Global', 'Kids'];
							return getParamLabels('category')
								.filter((category) => category.name !== 'Leeg' && category.name !== 'Empty')
								.sort((a, b) => {
									const indexA = desiredOrder.findIndex((name) => name === a.name);
									const indexB = desiredOrder.findIndex((name) => name === b.name);
									return indexA - indexB;
								}) // Sorteer de categorieën op basis van de gewenste volgorde
								.map((category, categoryIndex) => {
									const isActive = handleCategoryClass(category).includes('tag--active');
									return (
										<button
											key={categoryIndex}
											onClick={() => handleParamClick(category.value, 'category')}
											className={`${handleCategoryClass(category)} tag--sub-genre-mobile`}
											aria-pressed={isActive}
											aria-label={isActive ? `Filter: ${category.name}` : undefined}
										>
											<span className="tag__text">{category.name}</span>
										</button>
									);
								});
						})()}
					</div>
					<div className="agenda-filter-box__tags-wrap">
						{(() => {
							const desiredOrder = ['Klassiek', 'Classic', 'Pop', 'Jazz', 'Wereld', 'Global', 'Kids'];
							return genres
								.filter((category) => category.name !== 'Leeg' && category.name !== 'Empty')
								.sort((a, b) => {
									const indexA = desiredOrder.findIndex((name) => name === a.name);
									const indexB = desiredOrder.findIndex((name) => name === b.name);
									return indexA - indexB;
								}) // Sorteer genres op basis van de gewenste volgorde
								.map(({ name, subgenres }, genreIndex) => {
									const displayName = name === 'Leeg' ? 'Overig' : name;
									return (
										<div key={genreIndex} className="agenda-filter-box__tags-wrap">
											<h2 className="agenda-filter-box__title heading-time">{displayName}</h2>
											<div className="agenda-filter-box__tags-wrap-container">
												{subgenres.map((param, paramIndex) => {
													const isActive = handleTagClass(param).includes('tag--active');
													return (
														<button
															key={paramIndex}
															onClick={() => handleParamClick(param.value, 'tag')}
															className={`tag-filter ${handleTagClass(param)}`}
															aria-pressed={isActive}
															aria-label={isActive ? `Filter: ${param.name}` : undefined}
														>
															<span className="tag__text" dangerouslySetInnerHTML={{ __html: param.name }}></span>
														</button>
													);
												})}
											</div>
										</div>
									);
								});
						})()}
					</div>
				</div>
			)}
		</>
	);
};

export const filterTags = (memoizedActiveTags) => {
	return memoizedActiveTags.map((tag) => Object.values(tag)[0]);
};

// Filter logica voor categorieën (niet meer gebruikt in display container)
export const filterCategories = (memoizedActiveCategories) => {
	return memoizedActiveCategories.join(', ');
};

export default memo(FilterbarMob);
